// iter157 — Template Upload Dialog. Reusable preview + apply flow for the
// two excel templates built in iter155 Part C (Setup + Monthly Daily Entry).
//
// Props:
//   kind:   "setup" | "daily"
//   year?:  int (daily only)
//   month?: int (daily only)
//   onApplied: async callback after a successful batch POST (so the host
//              page can refetch its lists)
//   onClose:  dialog close callback

import { buildDailyPayload, saveErrorMessage } from "@/lib/dailyPayload";
import { useRef, useState } from "react";
import { Upload, CheckCircle2, AlertTriangle, X, FileSpreadsheet } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { parseSetupTemplate, parseDailyEntryTemplate } from "@/lib/excelTemplates";
import { profileApi } from "@/lib/profilesApi";
import { vendorApi, cchApi, dailyApi } from "@/lib/setupApi";

export default function TemplateUploadDialog({ kind, year, month, schoolId, activeProfile, onApplied, onClose }) {
  const inputRef = useRef(null);
  const [parsed, setParsed] = useState(null);
  const [applying, setApplying] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0, errors: [] });

  const pick = () => inputRef.current?.click();

  const handleFile = async (file) => {
    if (!file) return;
    try {
      if (kind === "setup") {
        const r = await parseSetupTemplate(file);
        setParsed({ ...r, kind: "setup" });
      } else {
        const r = await parseDailyEntryTemplate(file);
        setParsed({ ...r, kind: "daily" });
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err);
      toast.error("Could not parse the file — is it a template produced by this app?");
    }
  };

  const apply = async () => {
    if (!parsed) return;
    if (parsed.errors.length > 0) {
      toast.error("Fix the errors first — nothing will be applied while any row is red");
      return;
    }
    setApplying(true);
    const errors = [];
    try {
      if (parsed.kind === "setup") {
        // 1. Upsert the school profile
        if (parsed.profile && activeProfile?.id) {
          try {
            await profileApi.upsert({ ...activeProfile, ...parsed.profile }, activeProfile.id);
            setProgress((p) => ({ ...p, done: p.done + 1 }));
          } catch (e) { errors.push(`school profile: ${e?.response?.data?.detail || e?.message || "error"}`); }
        }
        // 2. Vendors — upsert each row (backend upsert dedupes by id when
        // supplied). We don't delete existing rows here — the preview UI
        // tells the user that this is an "add / update" operation, not a
        // destructive overwrite.
        for (const v of (parsed.vendors || [])) {
          try {
            // eslint-disable-next-line no-await-in-loop
            await vendorApi.upsert(v, v.id);
            setProgress((p) => ({ ...p, done: p.done + 1 }));
          } catch (e) { errors.push(`vendor ${v.name}: ${e?.response?.data?.detail || e?.message || "error"}`); }
        }
        // 3. CCHs
        for (const c of (parsed.cchs || [])) {
          try {
            // eslint-disable-next-line no-await-in-loop
            await cchApi.upsert(c, c.id);
            setProgress((p) => ({ ...p, done: p.done + 1 }));
          } catch (e) { errors.push(`cch ${c.name}: ${e?.response?.data?.detail || e?.message || "error"}`); }
        }
      } else {
        // Daily entries — upsert one by one (dailyApi.upsert is idempotent)
        for (const e of (parsed.entries || [])) {
          try {
            // eslint-disable-next-line no-await-in-loop
            await dailyApi.upsert(buildDailyPayload({ ...e, schoolId }), e.id || undefined);
            setProgress((p) => ({ ...p, done: p.done + 1 }));
          } catch (err) {
            // eslint-disable-next-line no-console
            console.error("[template] daily save failed", e.date, err?.response?.data, err);
            errors.push(`${e.date}: ${saveErrorMessage(err)}`);
          }
        }
      }
      if (errors.length === 0) {
        toast.success(`Applied ${progressTotalFor(parsed)} rows`);
      } else {
        toast.error(`${errors.length} row(s) failed — see details in the dialog`);
      }
      setProgress((p) => ({ ...p, errors }));
      if (onApplied) await onApplied();
      if (errors.length === 0) onClose?.();
    } finally {
      setApplying(false);
    }
  };

  const total = parsed ? progressTotalFor(parsed) : 0;

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-ink-900/60 p-4" data-testid={`template-upload-${kind}`}>
      <div className="w-full max-w-3xl overflow-hidden rounded-2xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-border bg-sand-50 px-5 py-3">
          <div>
            <h2 className="font-heading text-lg font-bold text-ink-900">
              Upload {kind === "setup" ? "Setup" : `Daily Entry (${year}-${String(month).padStart(2, "0")})`} Template
            </h2>
            <p className="text-xs text-ink-700">Preview parsed rows before writing anything to the database.</p>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} data-testid="template-upload-close">
            <X className="h-4 w-4" />
          </Button>
        </div>

        <div className="max-h-[60vh] overflow-y-auto p-5">
          {!parsed ? (
            <div className="rounded-xl border-2 border-dashed border-border p-8 text-center" data-testid="template-upload-dropzone">
              <FileSpreadsheet className="mx-auto h-12 w-12 text-clay" />
              <h3 className="mt-3 font-heading text-lg font-semibold text-ink-900">Drop the filled .xlsx here</h3>
              <p className="mt-1 text-xs text-ink-700">We'll parse, validate, and preview every row before applying.</p>
              <input
                ref={inputRef} type="file" accept=".xlsx" className="hidden"
                data-testid="template-upload-input"
                onChange={(e) => handleFile(e.target.files?.[0])}
              />
              <Button className="mt-4 bg-clay text-white hover:bg-clay-dark" onClick={pick} data-testid="template-upload-pick">
                <Upload className="mr-1 h-4 w-4" /> Choose .xlsx file
              </Button>
            </div>
          ) : (
            <div data-testid="template-upload-preview">
              <div className="flex items-center gap-2 rounded-lg bg-moss/10 px-3 py-2 text-sm text-moss-dark">
                <CheckCircle2 className="h-4 w-4" />
                Parsed <b className="font-semibold">{total}</b> row{total === 1 ? "" : "s"} — {parsed.errors.length === 0 ? "no errors" : `${parsed.errors.length} error${parsed.errors.length === 1 ? "" : "s"}`}.
              </div>

              {parsed.kind === "setup" && (
                <div className="mt-3 grid grid-cols-3 gap-2 text-xs">
                  <StatBox label="School Profile" value={parsed.profile ? 1 : 0} />
                  <StatBox label="Vendors" value={(parsed.vendors || []).length} />
                  <StatBox label="CCHs" value={(parsed.cchs || []).length} />
                </div>
              )}
              {parsed.kind === "daily" && (
                <div className="mt-3 grid grid-cols-3 gap-2 text-xs">
                  <StatBox label="Working" value={(parsed.entries || []).filter((e) => e.dayKind === "working").length} />
                  <StatBox label="No MDM" value={(parsed.entries || []).filter((e) => e.dayKind === "working_no_mdm").length} />
                  <StatBox label="Holiday" value={(parsed.entries || []).filter((e) => e.dayKind === "holiday").length} />
                </div>
              )}

              {parsed.errors.length > 0 && (
                <div className="mt-4 rounded-lg border border-rose-300 bg-rose-50 p-3" data-testid="template-upload-errors">
                  <p className="flex items-center gap-1 text-xs font-semibold text-rose-700">
                    <AlertTriangle className="h-3.5 w-3.5" />
                    Fix these rows and re-upload — nothing will be applied while any row is red.
                  </p>
                  <ul className="mt-2 max-h-40 overflow-y-auto space-y-1 text-xs text-rose-700">
                    {parsed.errors.map((er, i) => (
                      <li key={i} data-testid={`template-upload-error-${i}`}>
                        <b>{er.sheet} row {er.row}:</b> {er.msg}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {progress.errors.length > 0 && (
                <div className="mt-3 rounded-lg border border-rose-300 bg-rose-50 p-3" data-testid="template-upload-apply-errors">
                  <p className="text-xs font-semibold text-rose-700">Apply errors:</p>
                  <ul className="mt-1 max-h-32 overflow-y-auto text-xs text-rose-700">
                    {progress.errors.map((e, i) => <li key={i}>{e}</li>)}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center justify-between gap-2 border-t border-border bg-sand-50 px-5 py-3">
          <div className="text-xs text-ink-700" data-testid="template-upload-progress">
            {applying ? `Applying ${progress.done}/${total}…` : parsed ? `Ready to apply ${total} rows` : "Pick a .xlsx file to begin"}
          </div>
          <div className="flex gap-2">
            {parsed && (
              <Button variant="outline" size="sm" onClick={() => { setParsed(null); setProgress({ done: 0, total: 0, errors: [] }); }} data-testid="template-upload-reset">
                Pick a different file
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={onClose} data-testid="template-upload-cancel">Cancel</Button>
            <Button
              size="sm"
              disabled={!parsed || parsed.errors.length > 0 || applying}
              onClick={apply}
              className="bg-clay text-white hover:bg-clay-dark"
              data-testid="template-upload-apply"
            >
              {applying ? `Applying…` : `Apply ${total} rows`}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function StatBox({ label, value }) {
  return (
    <div className="rounded-lg border border-border bg-white p-2">
      <p className="text-[0.65rem] font-semibold uppercase tracking-wider text-ink-700">{label}</p>
      <p className="font-mono text-base font-bold text-ink-900">{value}</p>
    </div>
  );
}

function progressTotalFor(parsed) {
  if (parsed.kind === "setup") {
    return (parsed.profile ? 1 : 0) + (parsed.vendors?.length || 0) + (parsed.cchs?.length || 0);
  }
  return parsed.entries?.length || 0;
}
